/**
 * Implementation primitives for the per-platform **field-of-view** polygon
 * layer (PR5b inc3; REQ-UI-070; ADR-032 §4.2) — a thin per-kind binding over
 * the shared geometry-polygon mechanism in :file:`geometry-polygon-layer.ts`.
 * Split from :file:`fov-layer.tsx` per the ``react-refresh/only-export-components``
 * rule; the coverage / obscuration impls (inc4) are its siblings.
 *
 * One platform's status carries at most one field-of-view ring
 * (:type:`StatusGeometry.field_of_view`), so this kind's ring extractor
 * yields a 0-or-1-element list (``g => g.field_of_view ? [g.field_of_view] :
 * []``). The shared mechanism then renders ALL platforms' FoV polygons in
 * one source — one ``Polygon`` feature per platform whose latest status
 * carried a drawable FoV ring — as a faint desaturated fill (``--geom-fov``
 * at low opacity) plus a more-saturated outline (``--geom-fov-stroke``) so
 * the sector stays operator-visible once the fill drops to a wash (CLAUDE.md
 * §8.1). Both paints resolve through the inc1 design-system readers
 * (:func:`getGeomColor` / :func:`getGeomStrokeColor`) so the theme flip
 * carries through on the next paint (REQ-UI-040).
 *
 * The fill + line layers are inserted **below** the snapshot marker layer
 * (handled by the shared :func:`attachGeometryPolygonLayer`) so the platform
 * markers always paint on top of the geometry wash (per §8.1).
 *
 * # Why this file still exists after the inc4 factor-out
 *
 * The shared helper owns the ring → feature → source/fill/line mechanism
 * (CLAUDE.md §4.4 — written once, used by all three kinds). This file keeps
 * the FoV-specific bindings: the three layer IDs, the FoV ring extractor,
 * the FoV paint constants, and the stable :func:`attachFovLayer` /
 * :func:`detachFovLayer` / :func:`fovToFeatureCollection` names the inc3
 * tests + :file:`fov-layer.tsx` import.
 *
 * Cross-references:
 *   - REQ-UI-070 (SAPIENT map geometry — FoV layer) / REQ-UI-040 / REQ-UI-046.
 *   - ADR-032 §4.2 (Status geometry data model).
 *   - CLAUDE.md §4.4 (no duplicated logic) / §8.1 (markers above the wash).
 *   - map/geometry-polygon-layer.ts (the shared mechanism this delegates to).
 *   - map/marker-styling.ts (getGeomColor / getGeomStrokeColor).
 */
import type { Map as MapLibreMap } from "maplibre-gl";
import { getGeomColor, getGeomStrokeColor } from "@/map/marker-styling";
import type { StatusGeometry } from "@/map/sse-types";
import {
  type GeometryEntry,
  type GeometryFeatureProps,
  type RingExtractor,
  attachGeometryPolygonLayer,
  closeRing,
  detachGeometryPolygonLayer,
  ringsToPolygonFeatures,
} from "@/map/geometry-polygon-layer";

/** MapLibre GeoJSON source ID for the field-of-view polygons. */
export const FOV_SOURCE_ID = "meridian-fov-source";
/** MapLibre fill-layer ID for the field-of-view wash. */
export const FOV_FILL_LAYER_ID = "meridian-fov-fill-layer";
/** MapLibre line-layer ID for the field-of-view outline. */
export const FOV_LINE_LAYER_ID = "meridian-fov-line-layer";

/**
 * Fill opacity for the FoV wash — faint so the basemap + markers read
 * through it; the saturated outline carries the shape. Within the
 * ~0.12–0.2 band documented on :func:`getGeomColor`.
 */
export const FOV_FILL_OPACITY = 0.15;
/** Outline width in screen px — thin but legible against the wash. */
export const FOV_LINE_WIDTH_PX = 1.5;

/**
 * A platform's FoV entry as the feature builder consumes it. Re-exported
 * alias of the shared :type:`GeometryEntry` so the inc3 imports
 * (``FovEntry``) keep working unchanged.
 */
export type FovEntry = GeometryEntry;

/**
 * Pull the (0-or-1) FoV ring out of a platform's geometry. A ``null``
 * field-of-view (the platform reported none) yields an empty list, so the
 * shared builder emits no feature for it.
 */
const extractFovRings: RingExtractor = (geometry: StatusGeometry) =>
  geometry.field_of_view ? [geometry.field_of_view] : [];

// Re-export the shared ring-closer under its inc3 name so
// ``fov-layer-impl.test.ts`` keeps importing ``closeRing`` from here.
export { closeRing };

/**
 * Build the GeoJSON source payload — one closed, antimeridian-unwrapped
 * ``Polygon`` feature per platform whose ``geometry.field_of_view`` is a
 * drawable ring. Delegates to the shared :func:`ringsToPolygonFeatures` with
 * the FoV ring extractor (CLAUDE.md §4.4); platforms with a ``null`` /
 * too-short FoV are skipped.
 *
 * Properties carry ``producer_id`` (+ ``ring_index``, always 0 for FoV since
 * there's at most one ring). Exported for unit testability.
 */
export function fovToFeatureCollection(
  entries: readonly FovEntry[],
): GeoJSON.FeatureCollection<GeoJSON.Polygon, GeometryFeatureProps> {
  return ringsToPolygonFeatures(entries, extractFovRings);
}

/**
 * Register the FoV source + fill layer + line layer (idempotent: later calls
 * ``setData`` + re-apply the theme-resolved paint colours), painted **below**
 * the snapshot marker layer (CLAUDE.md §8.1). Delegates to the shared
 * :func:`attachGeometryPolygonLayer` with the FoV IDs + paints +
 * theme-resolved colours.
 *
 * Exported for unit testability. The :class:`FovLayer` component (in
 * :file:`fov-layer.tsx`) owns the React-effect lifecycle + the
 * visibility/zoom gate (via the shared :func:`useGeometryLayerEffect`).
 */
export function attachFovLayer(map: MapLibreMap, entries: readonly FovEntry[]): void {
  attachGeometryPolygonLayer(map, {
    sourceId: FOV_SOURCE_ID,
    fillLayerId: FOV_FILL_LAYER_ID,
    lineLayerId: FOV_LINE_LAYER_ID,
    features: fovToFeatureCollection(entries),
    fillColor: getGeomColor("fov"),
    strokeColor: getGeomStrokeColor("fov"),
    fillOpacity: FOV_FILL_OPACITY,
    lineWidth: FOV_LINE_WIDTH_PX,
  });
}

/**
 * Tear down the FoV fill + line layers and source. Delegates to the shared
 * :func:`detachGeometryPolygonLayer`. No-op when nothing is registered.
 */
export function detachFovLayer(map: MapLibreMap): void {
  detachGeometryPolygonLayer(map, {
    sourceId: FOV_SOURCE_ID,
    fillLayerId: FOV_FILL_LAYER_ID,
    lineLayerId: FOV_LINE_LAYER_ID,
  });
}
